import { IdleAnimation, IdleAnimationStage, waitForAnimationEnd } from './idle-animation';
import './shooting-star-animation.css';

// A small five-point star silhouette (fill: currentColor) that streaks across
// the whole page trailing a tail. Authored around a 24x24 viewBox.
const STAR_SVG = `
<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <path d="M12 1.5 L15 8.7 L22.5 9.3 L16.8 14.2 L18.6 21.5 L12 17.5 L5.4 21.5 L7.2 14.2 L1.5 9.3 L9 8.7 Z"/>
</svg>`;

/**
 * A brief "shooting stars" idle animation: a few stars streak diagonally across
 * the whole page background on staggered delays, each trailing a soft glow, then
 * fade out. A lightweight second entry in the {@link IdleAnimationDirector}
 * registry so the director has something to randomly rotate to besides the toy
 * parade.
 *
 * Runs on its own dedicated full-page stage (a fixed viewport-sized layer
 * behind content that always spans the entire page). Like the parade it is
 * entirely CSS-keyframe driven and removes every node it creates once the last
 * star finishes (or the pass is aborted).
 */
export class ShootingStarAnimation implements IdleAnimation {
  readonly name = 'shooting-star';
  // Ambient effect that streaks across the whole viewport, so it uses the
  // dedicated full-page stage rather than the calendar-anchored page band.
  readonly stage: IdleAnimationStage = 'fullpage';

  // Staggered launch delays (ms) so the stars streak across in a loose volley
  // spread over the whole stage. The array length is the star count.
  private static readonly DELAYS: readonly number[] = [
    0, 300, 650, 1000, 1350, 1700, 2050, 2400,
    2750, 3100, 3450, 3800, 4150, 4500, 4850, 5200,
    5550, 5900, 6250, 6600, 6950, 7300, 7650, 8000,
    8350, 8700, 9050, 9400, 9750, 10100, 10450, 10800
  ];

  async play(stage: HTMLElement, signal: AbortSignal): Promise<void> {
    if (signal.aborted) {
      return;
    }

    // Measure the actual stage so trajectories are computed from its real size
    // (the page stage is fixed + full-viewport, so this ≈ the window box).
    const rect = stage.getBoundingClientRect();
    const width = rect.width || window.innerWidth;
    const height = rect.height || window.innerHeight;
    // How far off-screen the stars enter/exit so they never pop in/out on-screen.
    const margin = 80;

    const fragment = document.createDocumentFragment();
    const stars = ShootingStarAnimation.DELAYS.map((delay) => {
      const star = document.createElement('div');
      star.className = 'shooting-star';
      star.style.animationDelay = `${delay}ms`;

      const t = ShootingStarAnimation.trajectory(width, height, margin);
      // Feed the computed start/end offsets (px) to the CSS keyframe, which
      // translates the star from start to end — a straight corner-to-corner run.
      star.style.setProperty('--star-start-x', `${t.startX}px`);
      star.style.setProperty('--star-start-y', `${t.startY}px`);
      star.style.setProperty('--star-end-x', `${t.endX}px`);
      star.style.setProperty('--star-end-y', `${t.endY}px`);

      // Travel direction of this star. The tail glow is authored pointing
      // up-left (i.e. behind a star travelling down-right at 45°), so rotating
      // the body by (actualAngle - 45°) keeps the tail opposite the trajectory
      // for every star regardless of its individual angle.
      const angleDeg = (Math.atan2(t.endY - t.startY, t.endX - t.startX) * 180) / Math.PI;
      star.style.setProperty('--star-tail-rotate', `${angleDeg - 45}deg`);

      star.innerHTML = `<div class="shooting-star__body">${STAR_SVG}</div>`;
      fragment.appendChild(star);
      return star;
    });

    stage.appendChild(fragment);

    // The last star has the largest delay, so its streak finishes last.
    const last = stars[stars.length - 1];
    await waitForAnimationEnd(last, 'shooting-star-streak', signal);

    for (const star of stars) {
      star.remove();
    }
  }

  /**
   * Computes one star's straight-line trajectory. Every star enters from the
   * LEFT edge (anywhere down its full height) and travels rightward at an angle
   * randomly chosen in the [0°, 45°] range measured below the horizontal, i.e.
   * from purely horizontal (right) down to a 45° down-right diagonal. Spreading
   * the entry point across the whole left edge and the angle across the full
   * range makes the streaks fan out over the stage rather than clustering on one
   * line.
   *
   * The trajectory is extended far enough (a full stage diagonal past the entry)
   * that the star always exits off-screen through the right or bottom edge.
   * Start/end are pushed `margin` px beyond the edges so stars enter and exit
   * off-screen. Offsets are relative to the star's CSS anchor at the top-left of
   * the stage.
   */
  private static trajectory(
    width: number,
    height: number,
    margin: number
  ): { startX: number; startY: number; endX: number; endY: number } {
    // Entry point: on the left edge (pushed off-screen by `margin`), anywhere
    // down the full height so streaks start high or low.
    const startX = -margin;
    const startY = ShootingStarAnimation.rand(-margin, height);

    // Movement angle constrained to [0°, 45°] below the horizontal: 0 rad is
    // straight right, MAX_ANGLE (π/4) is a 45° down-right diagonal.
    const angle = ShootingStarAnimation.rand(0, ShootingStarAnimation.MAX_ANGLE);

    // Project the trajectory well past the stage so it always exits off-screen.
    const reach = width + height + margin * 2;
    const endX = startX + Math.cos(angle) * reach;
    const endY = startY + Math.sin(angle) * reach;

    return { startX, startY, endX, endY };
  }

  // Largest movement angle off the horizontal (radians). π/4 = 45°, so stars
  // travel between straight-right (0°) and a down-right diagonal (45°).
  private static readonly MAX_ANGLE = Math.PI / 4;

  private static rand(min: number, max: number): number {
    return min + Math.random() * (max - min);
  }
}
